import { useRef, useState } from 'preact/hooks';

import { useAdsPlayerStateForLiveStream, useAdsPlayerStateForVideo } from '@/components/advertisements/hooks';
import { HabitatListItem } from '@/queries/habitats/types';
import { Video, VideoV2 } from '@/queries/media/types';
import { useSelector } from '@/redux/helper';
import { selectIsGuestSessionLocked } from '@/redux/slices/deviceSettings/selectors';

import HomeHighlightVideoPlayer from './HomeHighlightVideoPlayer';
import HomeLiveStreamPlayer from './HomeLiveStreamPlayer';

export type MiniPlayerContentMode = 'live-stream' | 'highlight';

interface HomeVideoSectionProps {
  highlight: Video | VideoV2 | undefined;
  preferMuted?: boolean;
  habitat: HabitatListItem | undefined;
  miniPlayerContentMode: MiniPlayerContentMode;
  onNextHighlight: () => void;
  onSwitchContentMode: () => void;
  onCloseMiniPlayer: () => void;
  onMuteChange?: (muted: boolean) => void;
  isShareModalOpen: boolean;
  isAmbientMode?: boolean;
  onAmbientModeChange?: (isAmbientMode: boolean) => void;
}

const HomeVideoSection = ({
  highlight,
  habitat,
  miniPlayerContentMode,
  preferMuted,
  onNextHighlight,
  onSwitchContentMode,
  onCloseMiniPlayer,
  onMuteChange,
  isShareModalOpen,
  isAmbientMode,
  onAmbientModeChange,
}: HomeVideoSectionProps) => {
  const preferLiveStreamMutedRef = useRef(true);
  const [streamKey, setStreamKey] = useState<string | undefined>(undefined);
  const [adsPlayer, setAdsPlayer] = useState<HTMLVideoElement | null>(null);
  const isLocked = useSelector((state) => selectIsGuestSessionLocked(state, null));
  const adsPlayerStateForVideo = useAdsPlayerStateForVideo(
    adsPlayer,
    miniPlayerContentMode === 'highlight' || isShareModalOpen ? undefined : highlight,
  );
  const adsPlayerStateForLiveStream = useAdsPlayerStateForLiveStream(
    adsPlayer,
    miniPlayerContentMode === 'highlight' && habitat && !isShareModalOpen ? habitat : undefined,
    streamKey,
  );

  const { canPlayVideo } = miniPlayerContentMode === 'highlight' ? adsPlayerStateForLiveStream : adsPlayerStateForVideo;

  if (isLocked) {
    return <div className="aspect-video bg-black" />;
  }

  return (
    <div className="group/home-video-section preflight relative">
      {highlight && (
        <HomeHighlightVideoPlayer
          highlight={highlight}
          miniMode={miniPlayerContentMode === 'highlight'}
          onSwitchContentMode={onSwitchContentMode}
          onNextHighlight={onNextHighlight}
          onClose={onCloseMiniPlayer}
          muted={preferMuted || miniPlayerContentMode === 'highlight'}
          onMuteChange={onMuteChange}
          hideOnMiniMode={!canPlayVideo}
          canPlayVideo={adsPlayerStateForVideo.canPlayVideo}
          setAdsPlayer={setAdsPlayer}
          advertisement={adsPlayerStateForVideo.advertisement}
        />
      )}

      {habitat && (
        <HomeLiveStreamPlayer
          habitat={habitat}
          miniMode={miniPlayerContentMode === 'live-stream'}
          preferLiveStreamMutedRef={preferLiveStreamMutedRef}
          onSwitchContentMode={onSwitchContentMode}
          onCameraChange={(camera) => setStreamKey(camera?.streamKey)}
          hideOnMiniMode={!canPlayVideo}
          canPlayVideo={adsPlayerStateForLiveStream.canPlayVideo}
          setAdsPlayer={setAdsPlayer}
          advertisement={adsPlayerStateForLiveStream.advertisement}
          isAmbientMode={isAmbientMode}
          onAmbientModeChange={onAmbientModeChange}
        />
      )}
    </div>
  );
};

export default HomeVideoSection;
